import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { Box, Check, Download, FileArchive, FileCode2, LoaderCircle, Printer, X } from "lucide-react";
import { cadClient } from "../cad/client";
import { createObj } from "../exporters/obj";
import { createThreeMf } from "../exporters/threeMf";
import { useModelStore } from "../state/store";
import { createMotivePackage, motiveFilename, saveBlobWithPicker } from "../storage/motivePackage";

type Format = "motive" | "step" | "3mf" | "stl" | "obj";

const formats: Array<{ id: Format; title: string; description: string; extension: string; icon: React.JSX.Element; recommended?: boolean }> = [
  { id: "motive", title: "ModelMotive project", description: "Preserves features, parameters and editable design intent.", extension: ".motive", icon: <FileArchive size={21} /> },
  { id: "3mf", title: "3MF print model", description: "Recommended for modern slicers; stores millimetre units and multiple bodies.", extension: ".3mf", icon: <Printer size={21} />, recommended: true },
  { id: "step", title: "STEP solid", description: "Exact CAD geometry for manufacturing and other CAD programs.", extension: ".step", icon: <Box size={21} /> },
  { id: "stl", title: "Binary STL", description: "Widely supported triangle mesh for 3D printing.", extension: ".stl", icon: <Download size={21} /> },
  { id: "obj", title: "Wavefront OBJ", description: "Triangle mesh for general 3D interchange.", extension: ".obj", icon: <FileCode2 size={21} /> }
];

function safeBaseName(name: string): string {
  return name.trim().replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "") || "model";
}

export function ExportDialog({ open, onOpenChange, initialFormat = "3mf" }: { open: boolean; onOpenChange: (open: boolean) => void; initialFormat?: Format }): React.JSX.Element {
  const document = useModelStore((state) => state.document);
  const revision = useModelStore((state) => state.revision);
  const currentEvaluation = useModelStore((state) => state.evaluation);
  const [selected, setSelected] = useState<Format>(initialFormat);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportSelected = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const base = safeBaseName(document.name);
      if (selected === "motive") {
        await saveBlobWithPicker(createMotivePackage(document), motiveFilename(document), "ModelMotive project", ".motive");
      } else if (selected === "step" || selected === "stl") {
        const blob = await cadClient.export(document, revision, selected);
        await saveBlobWithPicker(blob, `${base}.${selected}`, selected === "step" ? "STEP solid" : "STL mesh", `.${selected}`);
      } else {
        const evaluation = await cadClient.evaluate(document, revision, "final").catch(() => currentEvaluation);
        if (!evaluation) throw new Error("The model has not been rebuilt yet");
        if (selected === "3mf") await saveBlobWithPicker(createThreeMf(evaluation, document.name), `${base}.3mf`, "3MF print model", ".3mf");
        else await saveBlobWithPicker(createObj(evaluation, document.name), `${base}.obj`, "Wavefront OBJ", ".obj");
      }
      onOpenChange(false);
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content export-dialog">
          <div className="dialog-header"><div><Dialog.Title>Export model</Dialog.Title><Dialog.Description>Generated entirely on this device.</Dialog.Description></div><Dialog.Close asChild><button className="icon-button"><X size={17} /></button></Dialog.Close></div>
          <div className="format-list">
            {formats.map((format) => (
              <button key={format.id} className={`format-card ${selected === format.id ? "selected" : ""}`} onClick={() => setSelected(format.id)}>
                <span className="format-icon">{format.icon}</span>
                <span><strong>{format.title}{format.recommended && <em>Recommended</em>}</strong><small>{format.description}</small><code>{format.extension}</code></span>
                <span className="format-check">{selected === format.id && <Check size={15} />}</span>
              </button>
            ))}
          </div>
          {error && <div className="dialog-error">{error}</div>}
          <div className="dialog-footer"><button className="button ghost" onClick={() => onOpenChange(false)}>Cancel</button><button className="button primary" disabled={busy} onClick={() => void exportSelected()}>{busy ? <LoaderCircle size={16} className="spin" /> : <Download size={16} />} {busy ? "Generating…" : `Export ${selected.toUpperCase()}`}</button></div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
